import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getUiErrorMessage, getUiErrorToastText, toApiError } from '@/shared/api';
import { useStoresQuery } from '@/features/stores';
import { toast } from '@/shared/ui';

import {
  fetchOfflineProducts,
  useAddOfflineCartItemMutation,
  useApplyOfflineCouponMutation,
  useCheckoutOfflineOrderMutation,
  useClearOfflineCartMutation,
  useClearOfflineCustomerMutation,
  useIdentifyOfflineCustomerMutation,
  useOfflineCartQuery,
  useOfflineCouponsQuery,
  usePreviewOfflineCouponMutation,
  useRemoveOfflineCartItemMutation,
  useRemoveOfflineCouponMutation,
  useUpdateOfflineCartItemMutation,
} from '../api/offlineOrdersApi';
import { offlineOrderKeys } from '../api/keys';
import {
  addressDraftToCheckout,
  isSuccessfulOfflineCheckout,
  pruneOfflineDeliveryAddress,
} from '../lib/checkoutBody';
import { nextScanQuantity } from '../lib/nextScanQuantity';
import { buildOfflineSaleBill } from '../lib/saleBill';
import { readOrCreateSaleIdempotencyKey, rotateSaleIdempotencyKey } from '../lib/saleIdempotency';
import { checkoutCustomerSchema, offlinePhoneSchema } from '../schemas/identifyCustomerSchema';
import {
  emptyOfflineAddressDraft,
  emptyOfflineSession,
  OFFLINE_MAX_QUANTITY,
  type OfflineAddressDraft,
  type OfflineCouponPreview,
  type OfflineCustomerSnapshot,
  type OfflineFulfillmentType,
  type OfflineProduct,
  type OfflineSaleBill,
  type OfflineSaleSession,
} from '../types';

export type BarcodeScanOutcome =
  | { kind: 'added'; name: string }
  | { kind: 'none' }
  | { kind: 'many'; items: OfflineProduct[] }
  | { kind: 'max' }
  | { kind: 'error'; message: string };

function cartLineQuantity(session: OfflineSaleSession, productId: string): number | undefined {
  return session.cart.items.find((item) => item.productId === productId)?.quantity;
}

export function useOfflineSaleState() {
  const { t } = useTranslation('offlineOrders');
  const queryClient = useQueryClient();
  const cartQuery = useOfflineCartQuery();
  const storesQuery = useStoresQuery({ params: { page: 1, limit: 100 } });
  const session = cartQuery.data ?? emptyOfflineSession();

  const addItem = useAddOfflineCartItemMutation();
  const updateItem = useUpdateOfflineCartItemMutation();
  const removeItem = useRemoveOfflineCartItemMutation();
  const clearCart = useClearOfflineCartMutation();
  const identifyCustomer = useIdentifyOfflineCustomerMutation();
  const clearCustomer = useClearOfflineCustomerMutation();
  const applyCoupon = useApplyOfflineCouponMutation();
  const removeCoupon = useRemoveOfflineCouponMutation();
  const previewCouponMutation = usePreviewOfflineCouponMutation();
  const checkout = useCheckoutOfflineOrderMutation();

  const [storeId, setStoreId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(true);
  const [pendingStoreId, setPendingStoreId] = useState<string | null>(null);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmCheckoutOpen, setConfirmCheckoutOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(readOrCreateSaleIdempotencyKey);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState<OfflineFulfillmentType>('IN_STORE');
  const [addressDraft, setAddressDraft] = useState<OfflineAddressDraft>(emptyOfflineAddressDraft);
  const [customerSnapshot, setCustomerSnapshot] = useState<OfflineCustomerSnapshot | null>(null);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [couponNotice, setCouponNotice] = useState<string | null>(null);
  const [couponPreview, setCouponPreview] = useState<OfflineCouponPreview | null>(null);
  const [identifyError, setIdentifyError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<OfflineSaleBill | null>(null);

  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current || !cartQuery.data) return;
    hydrated.current = true;
    if (cartQuery.data.cart.storeId) {
      setStoreId(cartQuery.data.cart.storeId);
      setPickerOpen(false);
    }
    setPhoneDraft(cartQuery.data.customerPhoneNumber);
    setNameDraft(cartQuery.data.customerName);
  }, [cartQuery.data]);

  const serverPhone = cartQuery.data?.customerPhoneNumber;
  const serverName = cartQuery.data?.customerName;

  useEffect(() => {
    if (!hydrated.current || serverPhone === undefined) return;
    setPhoneDraft(serverPhone);
  }, [serverPhone]);

  useEffect(() => {
    if (!hydrated.current || serverName === undefined) return;
    setNameDraft(serverName);
  }, [serverName]);

  useEffect(() => {
    if ((cartQuery.data?.couponDiscountPaise ?? 0) === 0) setAppliedCouponCode(null);
  }, [cartQuery.data?.couponDiscountPaise]);

  const couponsQuery = useOfflineCouponsQuery(
    {
      phoneNumber: session.customerPhoneNumber,
      cartValue: session.cart.cartValueRupees,
    },
    {
      enabled: session.customerRegistered && session.customerPhoneNumber.length > 0,
    },
  );

  const selectedStore = storesQuery.data?.items.find((store) => store.id === storeId) ?? null;
  const storeMismatch =
    session.cart.items.length > 0 &&
    session.cart.storeId.length > 0 &&
    storeId !== null &&
    session.cart.storeId !== storeId;

  const cartBusy =
    addItem.isPending ||
    updateItem.isPending ||
    removeItem.isPending ||
    clearCart.isPending ||
    identifyCustomer.isPending ||
    clearCustomer.isPending ||
    applyCoupon.isPending ||
    removeCoupon.isPending ||
    checkout.isPending;

  const currentSession = useCallback((): OfflineSaleSession => {
    return queryClient.getQueryData<OfflineSaleSession>(offlineOrderKeys.cart()) ?? session;
  }, [queryClient, session]);

  const reloadStock = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: offlineOrderKeys.cart() });
    await queryClient.invalidateQueries({ queryKey: [...offlineOrderKeys.all, 'search'] });
  }, [queryClient]);

  const refreshSearch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [...offlineOrderKeys.all, 'search'] });
  }, [queryClient]);

  const reportCartError = useCallback(
    (err: unknown) => {
      toast.error(getUiErrorToastText(t, err));
      if (toApiError(err).status === 422) void reloadStock();
    },
    [reloadStock, t],
  );

  const addProduct = useCallback(
    async (productId: string) => {
      if (!storeId || storeMismatch) return;
      const qty = nextScanQuantity(cartLineQuantity(currentSession(), productId));
      if (qty === null) {
        toast.error({ title: t('cart.maxQuantity', { max: OFFLINE_MAX_QUANTITY }) });
        return;
      }
      try {
        await addItem.mutateAsync({ storeId, productId, quantity: qty });
        refreshSearch();
      } catch (err) {
        reportCartError(err);
        throw err;
      }
    },
    [addItem, currentSession, refreshSearch, reportCartError, storeId, storeMismatch, t],
  );

  const scanBarcode = useCallback(
    async (raw: string): Promise<BarcodeScanOutcome> => {
      if (!storeId) return { kind: 'error', message: t('store.required') };
      if (storeMismatch) return { kind: 'error', message: t('cart.mismatch') };
      const query = raw.trim();
      if (!query) return { kind: 'none' };
      try {
        const result = await fetchOfflineProducts({
          storeId,
          query,
          page: 1,
          limit: 20,
          inStockOnly: false,
          sortBy: 'relevance',
        });
        if (result.items.length === 0) return { kind: 'none' };
        if (result.items.length > 1) return { kind: 'many', items: result.items };
        const product = result.items[0];
        if (!product) return { kind: 'none' };
        const qty = nextScanQuantity(cartLineQuantity(currentSession(), product.productId));
        if (qty === null) return { kind: 'max' };
        await addItem.mutateAsync({ storeId, productId: product.productId, quantity: qty });
        refreshSearch();
        return { kind: 'added', name: product.name };
      } catch (err) {
        if (toApiError(err).status === 422) void reloadStock();
        return { kind: 'error', message: getUiErrorMessage(t, err) };
      }
    },
    [addItem, currentSession, refreshSearch, reloadStock, storeId, storeMismatch, t],
  );

  const setQuantity = useCallback(
    async (productId: string, quantity: number) => {
      const next = Math.max(0, Math.min(OFFLINE_MAX_QUANTITY, Math.floor(quantity)));
      try {
        if (next === 0) {
          await updateItem.mutateAsync({ productId, quantity: 0 });
        } else {
          await updateItem.mutateAsync({ productId, quantity: next });
        }
        refreshSearch();
      } catch (err) {
        reportCartError(err);
      }
    },
    [refreshSearch, reportCartError, updateItem],
  );

  const removeLine = useCallback(
    async (productId: string) => {
      try {
        await removeItem.mutateAsync(productId);
        refreshSearch();
      } catch (err) {
        reportCartError(err);
      }
    },
    [refreshSearch, removeItem, reportCartError],
  );

  const resetFulfillmentDraft = useCallback(() => {
    setFulfillmentType('IN_STORE');
    setAddressDraft(emptyOfflineAddressDraft());
  }, []);

  const resetCustomerDrafts = useCallback(() => {
    setCustomerSnapshot(null);
    setCouponPreview(null);
    setCouponNotice(null);
    setAppliedCouponCode(null);
    setPhoneDraft('');
    setNameDraft('');
    setCheckoutError(null);
  }, []);

  const selectStore = useCallback(
    (nextId: string) => {
      const live = currentSession();
      if (live.cart.items.length > 0 && live.cart.storeId !== nextId) {
        setPendingStoreId(nextId);
        setConfirmSwitchOpen(true);
        return;
      }
      setStoreId(nextId);
      setPickerOpen(false);
      setCheckoutError(null);
      resetFulfillmentDraft();
    },
    [currentSession, resetFulfillmentDraft],
  );

  const confirmStoreSwitch = useCallback(async () => {
    if (!pendingStoreId) return;
    try {
      await clearCart.mutateAsync();
      setStoreId(pendingStoreId);
      setPickerOpen(false);
      setPendingStoreId(null);
      setConfirmSwitchOpen(false);
      resetCustomerDrafts();
      resetFulfillmentDraft();
    } catch (err) {
      reportCartError(err);
    }
  }, [clearCart, pendingStoreId, reportCartError, resetCustomerDrafts, resetFulfillmentDraft]);

  const confirmClearCart = useCallback(async () => {
    try {
      await clearCart.mutateAsync();
      setConfirmClearOpen(false);
      resetCustomerDrafts();
      resetFulfillmentDraft();
    } catch (err) {
      reportCartError(err);
    }
  }, [clearCart, reportCartError, resetCustomerDrafts, resetFulfillmentDraft]);

  const identify = useCallback(
    async (phoneNumber: string) => {
      const parsed = offlinePhoneSchema.safeParse(phoneNumber);
      if (!parsed.success) return;
      setIdentifyError(null);
      try {
        const result = await identifyCustomer.mutateAsync(parsed.data);
        setCustomerSnapshot(result.customer);
        setCouponPreview(null);
        setCouponNotice(null);
        if (result.session.customerPhoneNumber) setPhoneDraft(result.session.customerPhoneNumber);
        const identifiedName = result.session.customerName || result.customer?.fullName || '';
        if (identifiedName) setNameDraft(identifiedName);
        if (!result.session.customerRegistered) setAppliedCouponCode(null);
      } catch (err) {
        setIdentifyError(getUiErrorMessage(t, err));
      }
    },
    [identifyCustomer, t],
  );

  const forgetCustomer = useCallback(async () => {
    try {
      await clearCustomer.mutateAsync();
      setCustomerSnapshot(null);
      setCouponPreview(null);
      setCouponNotice(null);
      setAppliedCouponCode(null);
      setPhoneDraft('');
      setNameDraft('');
    } catch (err) {
      reportCartError(err);
    }
  }, [clearCustomer, reportCartError]);

  const previewCoupon = useCallback(
    async (couponCode: string) => {
      const code = couponCode.trim();
      if (!code || !session.customerRegistered) return;
      setCouponNotice(null);
      try {
        const result = await previewCouponMutation.mutateAsync(code);
        if (!result.valid) {
          setCouponPreview(null);
          setCouponNotice(result.message || t('coupons.invalid'));
          return;
        }
        setCouponPreview(result);
      } catch (err) {
        setCouponPreview(null);
        setCouponNotice(getUiErrorMessage(t, err));
      }
    },
    [previewCouponMutation, session.customerRegistered, t],
  );

  const applyCouponCode = useCallback(
    async (couponCode: string) => {
      const code = couponCode.trim();
      if (!code || !session.customerRegistered) return;
      setCouponNotice(null);
      try {
        await applyCoupon.mutateAsync(code);
        setAppliedCouponCode(code);
        setCouponPreview(null);
        toast.success({ title: t('coupons.applied', { code }) });
      } catch (err) {
        setCouponNotice(getUiErrorMessage(t, err));
      }
    },
    [applyCoupon, session.customerRegistered, t],
  );

  const clearCoupon = useCallback(async () => {
    try {
      await removeCoupon.mutateAsync();
      setAppliedCouponCode(null);
      setCouponPreview(null);
      setCouponNotice(null);
    } catch (err) {
      setCouponNotice(getUiErrorMessage(t, err));
    }
  }, [removeCoupon, t]);

  const requestCheckout = useCallback(() => {
    if (!storeId || session.cart.items.length === 0 || storeMismatch || cartBusy) return;
    const parsed = checkoutCustomerSchema.safeParse({
      customerName: nameDraft,
      phoneNumber: phoneDraft,
    });
    if (!parsed.success) {
      const key = parsed.error.issues[0]?.message ?? 'customer.phone.invalid';
      setCheckoutError(t(key as 'customer.phone.invalid'));
      return;
    }
    setCheckoutError(null);
    setConfirmCheckoutOpen(true);
  }, [cartBusy, nameDraft, phoneDraft, session.cart.items.length, storeId, storeMismatch, t]);

  const confirmCheckout = useCallback(async () => {
    if (!storeId) return;
    const snapshot = currentSession();
    const soldAt = new Date().toISOString();
    const customerName = nameDraft.trim() || undefined;
    const phoneNumber = phoneDraft.trim() || undefined;
    const homeDelivery = fulfillmentType === 'HOME_DELIVERY';
    const deliveryAddress = homeDelivery
      ? pruneOfflineDeliveryAddress(addressDraftToCheckout(addressDraft), {
          customerName,
          phoneNumber,
        })
      : undefined;
    try {
      const result = await checkout.mutateAsync({
        storeId,
        idempotencyKey,
        customerName,
        phoneNumber,
        fulfillmentType: homeDelivery ? 'HOME_DELIVERY' : undefined,
        deliveryAddress,
      });
      setConfirmCheckoutOpen(false);
      if (
        !isSuccessfulOfflineCheckout(result.status, homeDelivery ? 'HOME_DELIVERY' : 'IN_STORE')
      ) {
        setCheckoutError(
          result.message ||
            t(homeDelivery ? 'checkout.homeDelivery.notPicking' : 'checkout.notDelivered'),
        );
        return;
      }
      let cleared = false;
      try {
        await clearCart.mutateAsync();
        cleared = true;
      } catch (clearErr) {
        toast.error(getUiErrorToastText(t, clearErr));
        setCheckoutError(
          t(homeDelivery ? 'checkout.homeDelivery.clearFailed' : 'checkout.clearFailed'),
        );
      }
      if (cleared) {
        setIdempotencyKey(rotateSaleIdempotencyKey());
        resetCustomerDrafts();
        resetFulfillmentDraft();
      }
      setReceipt(
        buildOfflineSaleBill({
          result,
          session: snapshot,
          store: selectedStore,
          customerName: nameDraft.trim(),
          customerPhone: phoneDraft.trim(),
          couponCode: appliedCouponCode,
          soldAt,
          fulfillmentType: homeDelivery ? 'HOME_DELIVERY' : 'IN_STORE',
          deliveryAddress,
        }),
      );
    } catch (err) {
      setConfirmCheckoutOpen(false);
      setCheckoutError(getUiErrorMessage(t, err));
      if (toApiError(err).status === 422) await reloadStock();
    }
  }, [
    addressDraft,
    appliedCouponCode,
    checkout,
    clearCart,
    currentSession,
    fulfillmentType,
    idempotencyKey,
    nameDraft,
    phoneDraft,
    reloadStock,
    resetCustomerDrafts,
    resetFulfillmentDraft,
    selectedStore,
    storeId,
    t,
  ]);

  const dismissReceipt = useCallback(() => {
    setReceipt(null);
    resetFulfillmentDraft();
  }, [resetFulfillmentDraft]);

  return {
    cartQuery,
    storesQuery,
    couponsQuery,
    session,
    selectedStore,
    storeId,
    pickerOpen,
    setPickerOpen,
    storeMismatch,
    cartBusy,
    phoneDraft,
    setPhoneDraft,
    nameDraft,
    setNameDraft,
    fulfillmentType,
    setFulfillmentType,
    addressDraft,
    setAddressDraft,
    customerSnapshot,
    identifyError,
    appliedCouponCode,
    couponNotice,
    couponPreview,
    checkoutError,
    receipt,
    confirmSwitchOpen,
    setConfirmSwitchOpen,
    confirmClearOpen,
    setConfirmClearOpen,
    confirmCheckoutOpen,
    setConfirmCheckoutOpen,
    pendingStoreId,
    identifying: identifyCustomer.isPending,
    clearingCustomer: clearCustomer.isPending,
    clearingCart: clearCart.isPending,
    couponBusy: previewCouponMutation.isPending || applyCoupon.isPending || removeCoupon.isPending,
    checkingOut: checkout.isPending,
    selectStore,
    confirmStoreSwitch,
    confirmClearCart,
    addProduct,
    scanBarcode,
    setQuantity,
    removeLine,
    identify,
    forgetCustomer,
    previewCoupon,
    applyCouponCode,
    clearCoupon,
    requestCheckout,
    confirmCheckout,
    dismissReceipt,
    reloadCart: () => cartQuery.refetch(),
  };
}
